/**
 * Provider SWR avec configuration globale optimisee pour PWA
 *
 * Centralise la configuration SWR pour garantir un comportement coherent
 * sur toute l'application, avec des defaults adaptes aux contraintes PWA.
 *
 * Configuration optimisee pour :
 * - Revalidation automatique au retour sur l'application
 * - Gestion intelligente des erreurs avec retry sur erreurs serveur
 * - Deduplication des requetes pour eviter le spam
 * - Throttle sur les revalidations au focus
 *
 * @module SWRProvider
 * @author Equipe Brydge
 * @since 2.0.0
 */

import React from 'react';
import {SWRConfig} from 'swr';
import {isTransientFailure} from 'packages/common/utils/authFailure';

/**
 * Configuration SWR globale par defaut
 * Ces valeurs peuvent etre surchargees par les hooks individuels si necessaire
 * @constant {Object}
 */
const defaultConfig = {
  // Revalidation au retour sur l'onglet
  // Active pour garantir la fraicheur des donnees quand l'utilisateur revient
  revalidateOnFocus: true,

  // Revalidation a la reconnexion reseau
  // Critique pour les utilisateurs mobiles avec connectivite intermittente
  revalidateOnReconnect: true,

  // Deduplication des requetes sur 2 secondes
  // Evite les appels multiples lors de transitions rapides entre composants
  dedupingInterval: 2000,

  // Throttle des revalidations sur focus a 5 secondes
  // Evite le spam de requetes lors de changements d'onglets frequents
  focusThrottleInterval: 5000,

  // Pas de polling automatique par defaut
  // Les hooks individuels peuvent l'activer si necessaire pour des donnees temps reel
  refreshInterval: 0,

  // Pas de refresh en arriere-plan pour economiser les ressources
  refreshWhenHidden: false,
  refreshWhenOffline: false,

  // Retry intelligent sur erreur
  // 3 tentatives avec 5 secondes d'intervalle
  errorRetryCount: 3,
  errorRetryInterval: 5000,

  // Seuls les échecs passagers se retentent, et la règle qui le décide vit à un
  // seul endroit : `utils/authFailure`. SWR est la couche qui retente les
  // données, l'intercepteur Axios ne double jamais ces tentatives.
  shouldRetryOnError: isTransientFailure,

  // Callback global d'erreur pour monitoring et debugging
  onError: (error, key) => {
    // Log structure pour faciliter le debugging en production
    if (process.env.NODE_ENV === 'development') {
      console.error('[SWR Error]', {
        key,
        message: error?.message,
        status: error?.response?.status,
      });
    }
  },
};

/**
 * Provider SWR avec configuration optimisee pour les applications PWA
 *
 * Doit wrapper l'application au niveau App.js pour garantir
 * que tous les hooks SWR heritent de la configuration globale.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Composants enfants
 * @param {Object} [props.config] - Configuration additionnelle a fusionner avec les defaults
 * @return {JSX.Element}
 *
 * @example
 * // Dans App.js
 * import {SWRProvider} from 'packages/common/providers/SWRProvider';
 *
 * function App() {
 *   return (
 *     <SWRProvider>
 *       <LocalizationProvider>
 *         <Router />
 *       </LocalizationProvider>
 *     </SWRProvider>
 *   );
 * }
 *
 * @example
 * // Avec configuration personnalisee
 * <SWRProvider config={{ errorRetryCount: 5 }}>
 *   <App />
 * </SWRProvider>
 */
export const SWRProvider = ({children, config = {}}) => {
  const mergedConfig = {
    ...defaultConfig,
    ...config,
  };

  return (
    <SWRConfig value={mergedConfig}>
      {children}
    </SWRConfig>
  );
};

export default SWRProvider;
